import { User } from "./User"

declare type SubscriptionStatus = "Active" | "Expired" | "HandledByOther" | "NotSubscribed";

export class Organization {
    id: string = ""
    name: string = ""
    isAdmin: boolean = false
    owners: User[] = []
    nxVetSubscriptionStatus: SubscriptionStatus = "NotSubscribed"
    nxCallSubscriptionStatus: SubscriptionStatus = "NotSubscribed"
    // ADR 0001 split-sub: NxHUB can be an org's ONLY subscription (e.g. bought
    // directly from the nx.vet storefront). Treat it as a real subscription.
    nxHubSubscriptionStatus: SubscriptionStatus = "NotSubscribed"
    isStripeNxHubSubscribedCache: boolean = false
    isNxVetSubscribed: boolean = false
    isNxCallSubscribed: boolean = false
    isStripeNxVetSubscribedCache: boolean = false
    isStripeNxCallSubscribedCache: boolean = false
    soapNoteRecipientEmail: string = ""
    nxVetForceSubscriptionEndDate: Date | undefined = undefined
    nxCallForceSubscriptionEndDate: Date | undefined = undefined
    isForceSubscription: boolean | undefined = undefined
    isShowingCompletePreferences: boolean = false

    phoneNumber: string = ""
    publicEmail: string = ""
    address: string = ""
    country: string = ""

    createdAt: Date | undefined = undefined
    updatedAt: Date | undefined = undefined
    createdBy: User | undefined = undefined
    updatedBy: User | undefined = undefined

    primaryLanguage: string = "";
    preferredMedicalRecordLanguage: string = "";
    patientManagementSoftware: string = "";
    otherPatientMangaugeSoftware: string = "";
    isEnablingOnMedicalRecordCompleteNotification: boolean = false;
    isRequiringRecordingConsent: boolean = false;
    reportIncludeCaseOwnerName: string | undefined = undefined;
    reportIncludeLogo: boolean = true;
    reportIncludeHospitalName: boolean = true;
    reportCustomHeaderText: string | null = null;
    reportIncludeHospitalInfo: boolean = true;
    reportIncludePhoneNumber: boolean = true;
    reportIncludeEmail: boolean = true;
    reportIncludeAddress: boolean = true;
    reportIncludePageNumber: boolean = true;
    reportIncludeDate: boolean = true;
    
    copyPasteFormat: string = "RichText";

    // When set, this org's medical record templates are managed by (read from)
    // the referenced organization and cannot be edited by this org's users.
    templateSourceOrganizationId?: string = undefined;
    templateSourceOrganizationName?: string = undefined;

    // Max duration (seconds) of a single NxHUB batch (manual) recording before
    // the device auto-stops. Admin-only; server clamps to 300–28800s. Default 3600.
    nxHubBatchMaxRecordingTimeS?: number = undefined;

    // When true, this org can access the Hypercare portal (/hypercare) and every
    // member sees the "Hypercare" nav link. Toggled admin-side from the org page.
    orgHasHypercareEnabled: boolean = false;

    get isNxHubSubscribed(): boolean {
        // Gate on the Stripe cache, NOT nxHubSubscriptionStatus: the backend's
        // CheckIsActive returns "HandledByOther" whenever the viewer isn't the
        // Stripe customer (always true for admins) BEFORE checking lifecycle,
        // so a canceled subscription still reads HandledByOther. The cache is
        // updated from actual subscription lifecycle events.
        return !!this.isStripeNxHubSubscribedCache;
    }

    get isSubscribed(): boolean {
        // A dedicated NxHUB sub counts as a full subscription (entitlement
        // decision 2026-08-11): it unlocks Review Center, Audio Recordings,
        // Documents, etc. the same way NxVET does. Legacy merged NxHUB orgs
        // are covered by the NxVET cache.
        return !!(this.isStripeNxVetSubscribedCache || this.isStripeNxCallSubscribedCache
            || this.isStripeNxHubSubscribedCache
            || (this.nxVetForceSubscriptionEndDate && this.nxVetForceSubscriptionEndDate > new Date())
            || (this.nxCallForceSubscriptionEndDate && this.nxCallForceSubscriptionEndDate > new Date()));
    }

    constructor(init?: Partial<Organization>) {
        if (!init) {
            return
        }

        Object.assign(this, init)

        if (init.owners) {
            this.owners = init.owners.map(o => new User(o))
        }

        if (init.soapNoteRecipientEmail) {
            this.soapNoteRecipientEmail = init.soapNoteRecipientEmail;
        }

        switch (this.nxVetSubscriptionStatus) {
            case "Active":
            case "Expired":
            case "HandledByOther":
                this.isNxVetSubscribed = true;
                break;
            default:
                this.isNxVetSubscribed = false;
                break;
        }

        switch (this.nxCallSubscriptionStatus) {
            case "Active":
            case "Expired":
            case "HandledByOther":
                this.isNxCallSubscribed = true;
                break;
            default:
                this.isNxCallSubscribed = false;
                break;
        }

        if (this.nxVetForceSubscriptionEndDate) {
            this.nxVetForceSubscriptionEndDate = new Date(this.nxVetForceSubscriptionEndDate);
        }

        if (this.nxCallForceSubscriptionEndDate) {
            this.nxCallForceSubscriptionEndDate = new Date(this.nxCallForceSubscriptionEndDate);
        }

        if (this.createdAt) {
            this.createdAt = new Date(this.createdAt);
        }

        if (this.updatedAt) {
            this.updatedAt = new Date(this.updatedAt);
        }
    }

    toString() {
        return this.name;
    }
}
